import { createApi } from '@reduxjs/toolkit/query/react';
import type { Dispatch } from '@reduxjs/toolkit';
import i18n from '../../../../i18n';
import { toast } from 'sonner';
import type {
  TAnyClientQuestionType,
  TAvailableLanguageCodes,
  ValidateTemplateChangesResponseDto,
  InterviewSummaryLayoutBlockDocument,
  TTranslatedQuestionLanguagesResponseDto,
} from '@hubertai/screening-question-types';
import type { TPaginationResult } from '../api-common.types';
import type {
  TAddQuestionsToBlockRequest,
  TAddQuestionToBlockRequest,
  TApplyAddSuggestionsRequest,
  TApplyAddSuggestionsResponse,
  TApplyAutomaticKnockoutRequest,
  TApplyEditSuggestionsRequest,
  TApplyEditSuggestionsResponse,
  TQuestionEditContextRequest,
  TQuestionEditContextResponse,
  TEditQuestionRequest,
  TEditQuestionResponse,
  TApplyEvaluationChangeRequest,
  TApplyRuleChangeRequest,
  TApplySuggestedChangesRequest,
  TApplySuggestedChangesResponse,
  TBuilderQuestionDto,
  TPaginationTemplateJobsRequest,
  TPaginationTemplateRevisionsRequest,
  TPaginationTemplatesRequest,
  TPaginatedTemplateListItem,
  TTemplatesRequest,
  TUpdatedTemplateResponse,
} from './templates.api.types';
import type { TCreateTemplatePayload } from 'components/Templates/builder/CreateTemplate/types';
import type { IAccessSettings } from 'components/Templates/builder/TemplateAccessSettings';
import config from 'config/index';
import { addManyToLibrary, addToLibrary, updateSwapOptionsToLibrary } from 'rtk/LibrarySlice';
import type { TTemplateAudienceProblem } from 'types/template/templateAudience.types';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import type { ETemplateRevision } from 'rtk/TemplateSlice';
import {
  addTemplate,
  pushToChangeLog,
  updateAccessGroupsVisibilityAndClientId,
  updateProcessId,
} from 'rtk/TemplateSlice';
import { EUserType } from 'types/accessControll.types';
import type { TCreateJobWithTemplateDTO } from 'types/dto/job.dto';
import { EEventType } from 'types/events.enum';
import type { TTemplate, TTemplateChangeLogItem } from 'types/template/template.types';

import type { TemplatePreviewData } from 'components/AgentChat/chat/types/templateWithSuggestedQuestions.types';

const reducerPath = 'templates' as const;
const path = 'v1/templates' as const;

export const templatesApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  tagTypes: ['Template', 'TemplateAccessGroups'],
  endpoints: (builder) => ({
    getTemplateById: builder.query<
      { template: TTemplate; accessGroupIds: string[]; questions?: TAnyClientQuestionType[] },
      { templateId: string }
    >({
      query: (x) => ({
        url: `/${x.templateId}?include=questions&include=client`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
      providesTags: (_, __, { templateId }) => [{ type: 'Template', id: templateId }],
      async onQueryStarted(_, { dispatch, queryFulfilled }: any) {
        try {
          const { data: responseData } = await queryFulfilled;
          const template: TTemplate = responseData.template;
          const accessGroupIds: string[] = responseData.accessGroupIds || [];
          const questions: TAnyClientQuestionType[] = responseData.questions;

          if (questions) {
            questions.map((question: TAnyClientQuestionType) => {
              dispatch(
                addToLibrary({
                  question,
                  cLanguage: template.language_settings.starting_language,
                }),
              );
            });
          }

          dispatch(
            addTemplate({
              templateId: template._id,
              template,
              accessGroupIds,
            }),
          );
        } catch (error) {
          toast.error(i18n.t('queryErrors.fetchTemplateWithDetail', { ns: 'Templates', error }));
        }
      },
    }),

    getTemplateQuestions: builder.query<any, { templateId: string }>({
      query: (x) => ({
        url: `/${x.templateId}/questions`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    getTemplateBuilderById: builder.query<
      {
        template: TTemplate;
        questions: TBuilderQuestionDto[];
        testJobId?: string;
        errors: any[];
        warning: any[];
        accessGroupIds?: string[];
        audienceProblems?: TTemplateAudienceProblem[];
      },
      {
        templateId: string;
        revision: ETemplateRevision;
        processId?: string;
      }
    >({
      query: (x) => ({
        url: `/${x.templateId}/builder`,
        method: 'POST',
        data: {
          revision: x.revision,
          processId: x.processId,
        },
        headers: {
          'X-Access-Control': EUserType.ANY,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }: any) {
        try {
          const { data: responseData } = await queryFulfilled;
          const template: TTemplate = responseData.template;
          const accessGroupIds: string[] = responseData.accessGroupIds || [];
          const questions: TBuilderQuestionDto[] = responseData.questions;
          const testJobId = responseData.testJobId;
          const swapOptions = responseData.questionBelongToAnotherClient;
          const hasQuestionsMissingLanguages = responseData?.hasQuestionsWithMissingLanguages ?? false;

          dispatch(
            addTemplate({
              templateId: template._id,
              template,
              testJobId,
              accessGroupIds,
              hasQuestionsWithMissingLanguages: hasQuestionsMissingLanguages,
              audienceProblems: responseData.audienceProblems,
            }),
          );

          if (questions) {
            // Read back the language addTemplate just resolved (it preserves the language the
            // user is currently viewing across a refetch of the same template) so newly loaded
            // question entries stay in sync with it instead of jumping back to the starting language.
            const currentLanguage = getState().Template.present.cLanguage.current;
            dispatch(
              addManyToLibrary({
                questions,
                cLanguage: currentLanguage,
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }

          // Example dispatch data to store inside api
          // dispatch( scenarioActions.addCurrentScenario( currentScen.data ))
        } catch {
          toast.error(i18n.t('queryErrors.fetchTemplate', { ns: 'Templates' }));
        }
      },
    }),

    getTemplateAccessGroups: builder.query<{ accessGroups: { _id: string; name: string }[] }, { templateId: string }>({
      query: (x) => ({
        url: `/${x.templateId}/access-groups`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
      providesTags: (_, __, { templateId }) => [{ type: 'TemplateAccessGroups', id: templateId }],
    }),

    createTemplateFromRevision: builder.mutation<
      { templateId: string; message?: string; warnings?: string[] },
      { processId: string; accessGroupIds?: string[] }
    >({
      query: (payload) => ({
        url: `/create-template-from-revision`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
      invalidatesTags: [{ type: 'Template', id: 'LIST' }],
    }),

    archiveTemplatesByProcessId: builder.mutation<
      {
        matchedCount: number;
        modifiedCount: number;
        message: string;
      },
      {
        processIds: string[];
      }
    >({
      query: (payload) => {
        return {
          url: `/archive-templates-by-process-id`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      invalidatesTags: [{ type: 'Template', id: 'LIST' }],
    }),

    deleteTemplatesByProcessId: builder.mutation<
      {
        matchedCount: number;
        modifiedCount: number;
        message: string;
      },
      {
        processIds: string[];
      }
    >({
      query: (payload) => {
        return {
          url: `/delete-templates-by-process-id`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      invalidatesTags: [{ type: 'Template', id: 'LIST' }],
    }),

    getCsatBytemplateId: builder.query<any, any>({
      query: (payload: { pageIndex: number; limit: number; id: string; filters: any }) => ({
        url: `/${payload.id}/csat-comments?pageIndex=${payload.pageIndex}&limit=${payload.limit}`,
        method: 'POST',
        data: payload.filters,
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
    }),

    createTemplate: builder.mutation<
      {
        templateId: string | null;
        isError?: boolean;
        message?: string;
        warnings?: string[];
      },
      TCreateTemplatePayload
    >({
      query: (payload) => ({
        url: `/create`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    publishInterviewTemplateDraft: builder.mutation<
      {
        statusCode: number;
        message: string;
      },
      {
        templateReferenceId: string;
        templateId: string;
        processId: string;
        revision: ETemplateRevision;
      }
    >({
      query: (payload) => ({
        url: `/publish-interview-template-draft`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    updateTemplateById: builder.mutation<
      TUpdatedTemplateResponse,
      {
        templateReferenceId: string;
        templateId: string;
        processId: string;
        revision: ETemplateRevision;
        template: TTemplate;
        changeLogItem?: TTemplateChangeLogItem;
        accessGroupIds?: string[];
      }
    >({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/update`,
          method: 'PATCH',
          data: payload,
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      async onQueryStarted(payload, { dispatch, queryFulfilled }: any) {
        try {
          const data = await queryFulfilled;
          const swapOptions = data?.data?.questionBelongToAnotherClient;
          console.log('updateTemplateById onQueryStarted', payload);
          if (swapOptions) {
            dispatch(
              updateSwapOptionsToLibrary({
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }
        } catch (error) {
          toast.error(i18n.t('queryErrors.fetchTemplateWithDetail', { ns: 'Templates', error }));
        }
      },
    }),

    addQuestionToBlock: builder.mutation<TUpdatedTemplateResponse, TAddQuestionToBlockRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/add-question`,
          method: 'POST',
          data: {
            processId: payload.processId,
            blockId: payload.blockId,
            questionId: payload.questionId,
            appendAfterQuestionId: payload?.appendAfterQuestionId,
          },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          const data = await queryFulfilled;
          const swapOptions = data?.data?.questionBelongToAnotherClient;
          if (swapOptions) {
            dispatch(
              updateSwapOptionsToLibrary({
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }
        } catch {
          toast.error(i18n.t('queryErrors.generic', { ns: 'Templates' }));
        }
      },
    }),
    applyAddSuggestions: builder.mutation<TApplyAddSuggestionsResponse, TApplyAddSuggestionsRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/apply-add-suggestions`,
          method: 'POST',
          data: { processId: payload.processId, items: payload.items },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          const data = await queryFulfilled;
          const swapOptions = data?.data?.questionBelongToAnotherClient;
          if (swapOptions) {
            dispatch(
              updateSwapOptionsToLibrary({
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }
        } catch {
          toast.error(i18n.t('queryErrors.generic', { ns: 'Templates' }));
        }
      },
    }),
    applyEditSuggestions: builder.mutation<TApplyEditSuggestionsResponse, TApplyEditSuggestionsRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/apply-edit-suggestions`,
          method: 'POST',
          data: { processId: payload.processId, items: payload.items },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
    }),
    // Read side of the AgentChat card's inline question editor: the question's current effective
    // content plus which parts of it this user may change. Both are decided server-side so the
    // client never re-derives the exclusivity/permission rules that govern them.
    getQuestionEditContext: builder.query<TQuestionEditContextResponse, TQuestionEditContextRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/question-edit-context`,
          method: 'POST',
          data: { processId: payload.processId, questionId: payload.questionId, language: payload.language },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
    }),
    // Write side. One question per request — see the note on TEditQuestionRequest.
    editQuestion: builder.mutation<TEditQuestionResponse, TEditQuestionRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/edit-question`,
          method: 'POST',
          data: {
            processId: payload.processId,
            questionId: payload.questionId,
            templateLanguage: payload.templateLanguage,
            content: payload.content,
            selectionCount: payload.selectionCount,
          },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
    }),
    applySuggestedChanges: builder.mutation<TApplySuggestedChangesResponse, TApplySuggestedChangesRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/apply-suggested-changes`,
          method: 'POST',
          data: {
            processId: payload.processId,
            addItems: payload.addItems,
            editItems: payload.editItems,
            removeItems: payload.removeItems,
          },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          const data = await queryFulfilled;
          const swapOptions = data?.data?.questionBelongToAnotherClient;
          if (swapOptions) {
            dispatch(
              updateSwapOptionsToLibrary({
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }
        } catch {
          toast.error(i18n.t('queryErrors.generic', { ns: 'Templates' }));
        }
      },
    }),
    applyRuleChange: builder.mutation<TUpdatedTemplateResponse, TApplyRuleChangeRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/apply-rule-change`,
          method: 'POST',
          data: {
            processId: payload.processId,
            questionId: payload.questionId,
            upsertRules: payload.upsertRules,
            removeRuleIds: payload.removeRuleIds,
          },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
    }),
    applyEvaluationChange: builder.mutation<TUpdatedTemplateResponse, TApplyEvaluationChangeRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/apply-evaluation-change`,
          method: 'POST',
          data: {
            processId: payload.processId,
            questionId: payload.questionId,
            optionId: payload.optionId,
            evaluation: payload.evaluation,
            removeRuleIds: payload.removeRuleIds,
          },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
    }),
    applyAutomaticKnockout: builder.mutation<TUpdatedTemplateResponse, TApplyAutomaticKnockoutRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/apply-automatic-knockout`,
          method: 'POST',
          data: {
            processId: payload.processId,
            questionId: payload.questionId,
            optionId: payload.optionId,
            rules: payload.rules,
          },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
    }),
    removeQuestionFromBlock: builder.mutation<TUpdatedTemplateResponse, TAddQuestionToBlockRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/remove-question`,
          method: 'POST',
          data: { processId: payload.processId, blockId: payload.blockId, questionId: payload.questionId },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          const data = await queryFulfilled;
          const swapOptions = data?.data?.questionBelongToAnotherClient;
          if (swapOptions) {
            dispatch(
              updateSwapOptionsToLibrary({
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }
        } catch {
          toast.error(i18n.t('queryErrors.removeQuestion', { ns: 'Templates' }));
        }
      },
    }),

    removeQuestionsFromBlock: builder.mutation<TUpdatedTemplateResponse, TAddQuestionsToBlockRequest>({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/remove-questions`,
          method: 'POST',
          data: { processId: payload.processId, questions: payload.questions },
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
        try {
          const data = await queryFulfilled;
          const swapOptions = data?.data?.questionBelongToAnotherClient;
          if (swapOptions) {
            dispatch(
              updateSwapOptionsToLibrary({
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }
        } catch {
          toast.error(i18n.t('queryErrors.removeQuestion', { ns: 'Templates' }));
        }
      },
    }),

    findPaginatedTemplates: builder.query<TPaginationResult<TPaginatedTemplateListItem>, TPaginationTemplatesRequest>({
      query: (payload) => {
        return {
          url: `/paginated-find`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: 'Template' as const, id: _id })),
              { type: 'Template', id: 'LIST' },
            ]
          : [{ type: 'Template', id: 'LIST' }],
    }),

    findTemplates: builder.query<{ templates: TTemplate[] }, TTemplatesRequest>({
      query: (payload) => {
        return {
          url: `/find`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.templates.map(({ _id }) => ({ type: 'Template' as const, id: _id })),
              { type: 'Template', id: 'LIST' },
            ]
          : [{ type: 'Template', id: 'LIST' }],
    }),

    getPaginatedTemplateJobs: builder.query<TPaginationResult<TTemplate>, TPaginationTemplateJobsRequest>({
      query: (x) => ({
        url: `/${x.filter.templateId}/jobs`,
        method: 'POST',
        data: x,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    findPaginatedTemplateRevisions: builder.query<TPaginationResult<TTemplate>, TPaginationTemplateRevisionsRequest>({
      query: (payload) => ({
        url: `/${payload.filter.templateId}/revisions`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    createJobWithTemplate: builder.mutation<{ success: boolean; jobId: string }, TCreateJobWithTemplateDTO>({
      query: (data) => {
        return {
          url: `/${data.templateId}/create-job`,
          method: 'POST',
          data,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    copyTemplatesToTestDB: builder.mutation<any, void>({
      query: () => {
        return {
          url: `/copy/templates-to-test-db`,
          method: 'GET',
          headers: {
            'X-Access-Control': EUserType.HUBERT,
          },
        };
      },
    }),

    refreshProcess: builder.mutation<unknown, { templateId: string; processId: string }>({
      query: (payload) => {
        return {
          url: `/${payload.templateId}/refresh-process`,
          method: 'POST',
          data: payload,
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
    }),

    updateTemplateAccessSettings: builder.mutation<
      {
        templateId: string;
        accessSettings: IAccessSettings;
        changeLogItem?: Partial<TTemplateChangeLogItem>;
      }[],
      {
        templateReferenceId: string;
        templateId: string;
        accessSettings: IAccessSettings;
        changeLogItem?: Partial<TTemplateChangeLogItem>;
      }
    >({
      query: (payload) => {
        return {
          url: `/${payload.templateReferenceId}/updateAccessSettings`,
          method: 'PATCH',
          data: payload,
          headers: {
            'X-Access-Control': EUserType.ANY,
          },
        };
      },
      invalidatesTags: (_, __, arg) => [
        { type: 'Template', id: arg.templateId },
        { type: 'Template', id: 'LIST' },
        { type: 'TemplateAccessGroups', id: arg.templateId },
      ],
      async onQueryStarted(
        payload,
        {
          dispatch,
          queryFulfilled,
        }: {
          dispatch: Dispatch;
          queryFulfilled: Promise<{
            data: {
              templateId: string;
              accessSettings: IAccessSettings;
              changeLogItem?: Partial<TTemplateChangeLogItem>;
            }[];
          }>;
        },
      ) {
        try {
          const { data: updatedAccessData } = await queryFulfilled;

          const currentUpdated = updatedAccessData?.find((item) => item.templateId === payload.templateId);

          if (currentUpdated) {
            dispatch(
              updateAccessGroupsVisibilityAndClientId({
                templateId: currentUpdated.templateId,
                eventType: EEventType.MODIFIED,
                resource: {
                  accessGroupIds: currentUpdated.accessSettings.accessGroupIds ?? [],
                  visibility: currentUpdated.accessSettings.visibility,
                  clientId: currentUpdated.accessSettings.clientId || '',
                },
              }),
            );

            if (currentUpdated.changeLogItem) {
              dispatch(
                pushToChangeLog({
                  templateId: currentUpdated.templateId,
                  item: currentUpdated.changeLogItem as TTemplateChangeLogItem,
                }),
              );
            }
          }
        } catch {
          toast.error(i18n.t('changeLog.accessSettings.saveError', { ns: 'Templates' }));
        }
      },
    }),

    getTemplatePreview: builder.query<TemplatePreviewData, { templateId: string; templateType?: ETemplateRevision }>({
      query: (payload) => {
        return {
          url: payload.templateType
            ? `/${payload.templateId}/minimal?templateType=${payload.templateType}`
            : `/${payload.templateId}/minimal`,
          method: 'GET',
          data: payload,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    getTemplateXlsx: builder.mutation<Blob, { processId: string }>({
      query: (payload) => {
        return {
          url: `/${payload.processId}/xlsx`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
          responseHandler: (response: any) => response.blob(),
          responseType: 'blob',
        };
      },
    }),

    validateTemplateChanges: builder.mutation<
      ValidateTemplateChangesResponseDto,
      { templateReferenceId: string; processId: string; userLanguage: string }
    >({
      query: (payload) => ({
        url: `/validate-template-changes`,
        method: 'POST',
        data: { body: payload },
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),
    reorderInterviewSummary: builder.mutation<
      TUpdatedTemplateResponse,
      {
        templateReferenceId: string;
        processId: string;
        summaryBlocks: InterviewSummaryLayoutBlockDocument[];
      }
    >({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/reorderInterviewSummary`,
          method: 'POST',
          data: {
            processId: payload.processId,
            summaryBlocks: payload.summaryBlocks,
          },
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    reorderQuestionOrder: builder.mutation<
      TUpdatedTemplateResponse,
      {
        templateReferenceId: string;
        processId: string;
        blocks: { id: string; questionIds: string[] }[];
      }
    >({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/reorderQuestionOrder`,
          method: 'POST',
          data: {
            processId: payload.processId,
            blocks: payload.blocks,
          },
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    swapQuestion: builder.mutation<
      TUpdatedTemplateResponse,
      {
        templateReferenceId: string;
        processId: string;
        questionId: string;
        copiedIdMap: Record<string, string>;
        copyBranching: boolean;
      }
    >({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/swap-question`,
          method: 'POST',
          data: {
            processId: payload.processId,
            questionId: payload.questionId,
            copiedIdMap: payload.copiedIdMap,
            copyBranching: payload.copyBranching,
          },
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      async onQueryStarted(_payload, { dispatch, queryFulfilled, getState }: any) {
        try {
          const { data } = await queryFulfilled;
          if (data.template) {
            const state = getState();
            dispatch(
              updateProcessId({
                processId: data.template.process_id,
              }),
            );
            dispatch(
              addTemplate({
                templateId: state.Template.present.cTemplateId,
                template: {
                  ...state.Template.present.c,
                  process_id: data.template.process_id,
                  outline: data.template.outline,
                  parameters: data.template.parameters,
                  blocks: data.template.blocks,
                  interview_summary: data.template.interview_summary,
                  change_log: data.template.change_log,
                  templateOutline: data.templateOutline,
                  evaluationSummary: data.evaluationSummary,
                  evaluation: data.template.evaluation,
                },
                audienceProblems: data.audienceProblems,
              }),
            );
          }
          const swapOptions = data?.questionBelongToAnotherClient;
          if (swapOptions) {
            dispatch(
              updateSwapOptionsToLibrary({
                questionBelongToAnotherClient: swapOptions,
              }),
            );
          }
        } catch {
          toast.error(i18n.t('queryErrors.swapQuestion', { ns: 'Templates' }));
        }
      },
    }),

    enableDisableQuestionOptions: builder.mutation<
      TUpdatedTemplateResponse,
      {
        templateReferenceId: string;
        processId: string;
        questionId: string;
        languageCode: TAvailableLanguageCodes;
        enabled: boolean;
        optionId: string;
      }
    >({
      query(payload) {
        return {
          url: `/${payload.templateReferenceId}/enable-disable-question-options`,
          method: 'POST',
          data: {
            processId: payload.processId,
            questionId: payload.questionId,
            languageCode: payload.languageCode,
            enabled: payload.enabled,
            optionId: payload.optionId,
          },
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    translateTemplateQuestions: builder.mutation<
      {
        translated: TTranslatedQuestionLanguagesResponseDto[];
        errors: { questionId: string; message: string }[];
      },
      { templateId: string; processId: string }
    >({
      query: (payload) => {
        return {
          url: `/${payload.templateId}/translate-questions`,
          method: 'POST',
          data: { processId: payload.processId },
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),
  }),
});

export const {
  // Queries

  useGetPaginatedTemplateJobsQuery,
  useGetTemplateAccessGroupsQuery,
  useFindTemplatesQuery,

  useGetTemplateBuilderByIdQuery,
  useGetTemplatePreviewQuery,

  // useGetPaginatedTemplatesQuery,

  // Paginated tables queries
  useFindPaginatedTemplatesQuery,

  useFindPaginatedTemplateRevisionsQuery,

  // Mutations
  // useUpdateTemplateBuilderByIdMutation,
  useUpdateTemplateByIdMutation,
  useUpdateTemplateAccessSettingsMutation,
  useAddQuestionToBlockMutation,
  useApplyAddSuggestionsMutation,
  useApplyEditSuggestionsMutation,
  useGetQuestionEditContextQuery,
  useEditQuestionMutation,
  useApplyEvaluationChangeMutation,
  useApplyRuleChangeMutation,
  useApplySuggestedChangesMutation,
  useRemoveQuestionFromBlockMutation,
  useRemoveQuestionsFromBlockMutation,

  useCopyTemplatesToTestDBMutation,
  useCreateTemplateMutation,
  usePublishInterviewTemplateDraftMutation,

  // Archive / create from revision
  useArchiveTemplatesByProcessIdMutation,
  useCreateTemplateFromRevisionMutation,
  // Delete process
  useDeleteTemplatesByProcessIdMutation,

  useCreateJobWithTemplateMutation,
  useRefreshProcessMutation,
  useReorderInterviewSummaryMutation,
  useReorderQuestionOrderMutation,
  useSwapQuestionMutation,
  useEnableDisableQuestionOptionsMutation,
  useGetTemplateXlsxMutation,
  useValidateTemplateChangesMutation,
  useTranslateTemplateQuestionsMutation,
} = templatesApi;
